/* ==========================================================================
   IRON FIT - Tool sets

   Tools are simple: one photo, a short spec, one price. The card carries the
   features and adds straight to the cart, and the name links to the kit's own
   page for the full bit list and the drive sizes.

   Copy and prices come from the live store's own product feed
   (03-PRODUCTS/products.json). The `key` on each kit is what the Shopify
   bridge maps to a real variant in ifz-shopify-cart.js - renaming one here
   without renaming it there empties the customer's cart at checkout, so the
   keys are left alone.

   ON THE SPEC FIELDS

   Everything in `spec` below is lifted from the kit's own feature copy, not
   invented. Where we have never written the number down - drive sizes,
   metric vs imperial, warranty term - the field is null and the comparison
   table drops that row entirely rather than printing a guess. A wrong socket
   size on a tool page is a refused delivery and a wasted trip across
   Lebanon, which costs more than the kit earns. Fill the nulls in and the
   rows appear on their own.
   ========================================================================== */
(function () {
  'use strict';

  /* The use-case a kit belongs to, for the picker above the grid. A kit can
     sit in more than one. */
  var USES = [
    { id: 'car',  label: 'Car & bike' },
    { id: 'home', label: 'Home & furniture' },
    { id: 'tech', label: 'Phones & electronics' },
    { id: 'gym',  label: 'Gym assembly' }
  ];

  /* The five kits, cheapest first — which is also smallest first, so the
     range reads as a ladder rather than a pile.

     `page` is the kit's own product page. Tools used to be card-only, on the
     argument that a socket set does not need a detail page; they have one now
     because they are the only things on the site a customer buys without an
     installer turning up to explain them, so the drive sizes and the bit list
     have to be readable somewhere. */
  var TOOLS = [
    {
      name: '46-Piece Socket Set',
      tag: 'The basics, done properly.',
      price: 5,
      img: 'assets/tool-socket46-red-2-760.jpg',
      key: 'tool-socket46',
      page: 'machine.html?m=tool-socket46',
      uses: ['car', 'home', 'gym'],
      features: [
        '46 pieces — 1/4" sockets, bit sockets, ratchet and extensions',
        'Quick-release ratchet, spinner handle and sliding T-bar',
        'Mirror-polished chrome vanadium',
        'Red hard case with a moulded tray, so nothing rattles loose'
      ],
      spec: {
        pieces: 46,
        steel: 'Chrome vanadium, mirror polished',
        caseType: 'Red hard case, moulded tray',
        bestFor: 'Car, bike and furniture bolts',
        drive: '1/4 inch',
        standard: 'Metric, 4–14 mm',
        warranty: null
      }
    },
    {
      name: '115-in-1 Precision Screwdriver Set',
      tag: 'For the things with screws you can barely see.',
      price: 8,
      img: 'assets/tool-precision115-black-2-760.jpg',
      key: 'tool-precision115',
      page: 'machine.html?m=tool-precision115',
      uses: ['tech'],
      features: [
        '115 hardened CR-V precision bits',
        'Driver with a non-slip grip and a rotating end cap — turn with one finger',
        'Flexible shaft, rigid extension and a right-angle adapter for tight corners',
        'Spudgers, a suction cup and tweezers for opening a phone without marking it'
      ],
      spec: {
        pieces: 115,
        steel: 'Hardened chrome vanadium (CR-V)',
        caseType: 'Black flip-open case',
        bestFor: 'Phones, laptops, consoles, watches, glasses',
        drive: '4 mm precision bits',
        standard: null,
        warranty: null
      }
    },
    {
      name: '53-Piece Socket & Tool Box',
      tag: 'A finer ratchet and room for more.',
      price: 12,
      img: 'assets/tool-box53-orange-2-760.jpg',
      key: 'tool-box53',
      page: 'machine.html?m=tool-box53',
      uses: ['car', 'home', 'gym'],
      features: [
        '53 pieces — sockets, bit sockets, extensions and a screwdriver handle',
        '24-tooth ratchet — 15° between clicks, so it still turns in a tight engine bay',
        'Spark plug socket and sliding T-bar included',
        'Orange carry case with a handle and two latches'
      ],
      spec: {
        pieces: 53,
        steel: 'Chrome vanadium, mirror polished',
        caseType: 'Orange carry case with handle',
        bestFor: 'Car and bike work in tight spaces',
        drive: '1/4 inch',
        standard: 'Metric',
        warranty: null
      }
    },
    {
      name: '128-in-1 Precision Bit Set',
      tag: 'Every head you will ever meet.',
      price: 15,
      img: 'assets/tool-repair128-grey-2-760.jpg',
      key: 'tool-repair128',
      page: 'machine.html?m=tool-repair128',
      uses: ['tech', 'home'],
      features: [
        '128 pieces — Phillips, Pozi, slotted, hex, Torx, security Torx, tri-wing, square, triangle and U-shape',
        'Hex bits in metric and imperial, so American kit is covered too',
        'Magnetiser and demagnetiser pad built into the case',
        'Folds down to 16 × 7.5 cm — the whole set fits in one hand'
      ],
      spec: {
        pieces: 128,
        steel: null,
        caseType: 'Grey fold-out case, 16 × 7.5 cm closed',
        bestFor: 'Phones, laptops, consoles, drones, watches',
        drive: '4 mm precision bits',
        standard: 'Metric and imperial hex',
        warranty: null
      }
    },
    {
      name: '108-Piece Car Repair Set',
      tag: 'The one that does the whole car.',
      price: 45,
      img: 'assets/tool-pro108-blue-2-760.jpg',
      key: 'tool-pro108',
      page: 'machine.html?m=tool-pro108',
      uses: ['car', 'home', 'gym'],
      features: [
        '108 pieces on both 1/4" and 1/2" drive — wheel nuts down to dashboard screws',
        'Sockets 4–32 mm, plus E10–E24 star sockets for European cars',
        'Two ratchets, universal joints, extension bars and sliding T-bars',
        'Spark plug sockets in 16 and 21 mm',
        'Blue heavy-duty case with a carry handle'
      ],
      spec: {
        pieces: 108,
        steel: 'Chrome vanadium, mirror finish',
        caseType: 'Blue heavy-duty latching case',
        bestFor: 'Car and motorbike, engine bay to wheels',
        drive: '1/4 inch and 1/2 inch',
        standard: 'Metric, 4–32 mm',
        warranty: null
      }
    }
  ];

  /* The comparison rows, in the order a buyer weighs them: how much is in
     it, what it is made of, what it lives in, what it is for, then price. */
  var ROWS = [
    { key: 'pieces',   label: 'Pieces',   fmt: function (v) { return v + ' pieces'; } },
    { key: 'drive',    label: 'Drive sizes' },
    { key: 'standard', label: 'Metric / imperial' },
    { key: 'steel',    label: 'Steel' },
    { key: 'caseType', label: 'Case' },
    { key: 'bestFor',  label: 'Best for' },
    { key: 'warranty', label: 'Warranty' }
  ];

  function money(n) { return window.IFZCart.money(n); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* The one place an item enters the cart. Shape is unchanged from the
     original page - IFZCart and the Shopify bridge both read it. */
  function addToCart(t) {
    window.IFZCart.add({
      type: 'tool',
      name: t.name,
      price: t.price,
      img: t.img,
      key: t.key,
      extras: [],
      includes: ['Delivery to your home - all Lebanon']
    });
  }

  /* ----------------------------------------------------------------------
     THE CARD
     ---------------------------------------------------------------------- */
  function card(t) {
    var root = el('article', 'acc acc--tool');
    root.setAttribute('data-uses', t.uses.join(' '));

    /* ---- photo, with the piece count over it ---- */
    /* A link, but hidden from assistive tech: the name below goes to the same
       page, and a screen reader announcing the same destination twice in a row
       is noise. The card cannot be one big <a> because it holds two buttons,
       and an anchor around a button is invalid and behaves differently in
       every browser. */
    var media = el('a', 'acc__media acc__media--tool');
    media.href = window.IFZU(t.page);
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    var pic = window.IFZPic(t.img);
    var img = pic.querySelector('img');
    img.alt = t.name;
    /* This page was pulling 951 KB of photographs, every one of them eager
       and far larger than the slot it lands in - the originals were 1043x764
       for a box about 355x266, nearly nine times the pixels. That download
       plus four main-thread decodes is what made it feel slow.

       The -760 files are the same shots resized to twice the display width,
       which is plenty for a retina phone and 55% lighter. Lazy defers what is
       below the fold, async keeps the decode off the main thread, and the
       intrinsic size reserves the space so the cards cannot jump. */
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 760;
    img.height = 557;
    media.appendChild(pic);

    if (t.spec.pieces) {
      var badge = el('span', 'tl__badge');
      badge.appendChild(el('b', null, String(t.spec.pieces)));
      badge.appendChild(el('em', null, 'pieces'));
      media.appendChild(badge);
    }

    /* ---- body ----

       Four things: photo, name, price, button. The card used to carry a
       category tag, four feature lines and a Case / Best-for pair as well,
       which made it 541 px tall on a phone against a 125 px photograph —
       a page of reading to scroll past five times before reaching a price.

       None of that is lost. The comparison table below holds every one of
       those attributes side by side, and the kit's own page holds the rest.
       A grid is for choosing which one to open; it is not the place to
       explain all five at once. */
    var body = el('span', 'acc__body');

    /* The name is the link to the kit's page — the one thing on the card a
       reader would expect to be clickable. */
    var name = el('a', 'acc__name tl__link', t.name);
    name.href = window.IFZU(t.page);
    body.appendChild(name);

    /* ---- price + the two calls to action ---- */
    var foot = el('span', 'acc__foot');
    var price = el('span', 'acc__price');
    /* Not "Delivered". A basket of nothing but kits adds $4, and the place to
       say that is next to the price rather than at the total, where it would
       read as a surprise. It disappears the moment a machine or a bench joins
       the basket — see IFZCart.delivery(). */
    price.innerHTML = money(t.price) + ' <em>+ $4 delivery</em>';
    foot.appendChild(price);
    body.appendChild(foot);

    /* One button, and it is the one that can be measured. "Ask on WhatsApp"
       used to sit beside it: every tap left the site for an app, so the sale
       never reached the Meta pixel and nothing could be optimised against.
       IFZCart.add fires AddToCart with the real value, so each of these is
       an event the ad account can learn from. WhatsApp is still in the
       header and the footer for anyone who wants to ask first. */
    var btn = el('button', 'acc__add', 'Add to cart');
    btn.type = 'button';
    var added = false;
    btn.addEventListener('click', function () {
      if (added) { window.location.href = window.IFZU('cart-revamp.html'); return; }
      addToCart(t);
      added = true;
      root.classList.add('is-added');
      btn.textContent = 'Added! View cart ->';
    });
    body.appendChild(btn);

    root.appendChild(media);
    root.appendChild(body);
    return root;
  }

  /* ----------------------------------------------------------------------
     THE COMPARISON TABLE

     At five products this replaces filtering entirely: the whole range and
     every attribute that separates it fits in one scan. A row where no kit
     has a value is dropped rather than printed as a line of em dashes.
     ---------------------------------------------------------------------- */
  function table() {
    var host = document.getElementById('toolTable');
    if (!host) return;

    var live = ROWS.filter(function (r) {
      return TOOLS.some(function (t) { return t.spec[r.key] != null; });
    });

    var tbl = el('table', 'tl__tbl');

    var thead = document.createElement('thead');
    var hr = document.createElement('tr');
    hr.appendChild(el('th', null, ''));
    TOOLS.forEach(function (t) {
      var th = document.createElement('th');
      th.scope = 'col';
      th.appendChild(document.createTextNode(t.name));
      th.appendChild(el('span', null, t.tag));
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    tbl.appendChild(thead);

    var tbody = document.createElement('tbody');

    live.forEach(function (r) {
      var tr = document.createElement('tr');
      var th = el('th', null, r.label);
      th.scope = 'row';
      tr.appendChild(th);
      TOOLS.forEach(function (t) {
        var v = t.spec[r.key];
        var td = document.createElement('td');
        if (v == null) {
          td.className = 'tl__na';
          td.textContent = '—';
        } else {
          td.textContent = r.fmt ? r.fmt(v) : v;
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });

    /* price */
    var pr = document.createElement('tr');
    var pth = el('th', null, 'Price');
    pth.scope = 'row';
    pr.appendChild(pth);
    TOOLS.forEach(function (t) {
      var td = document.createElement('td');
      var b = el('span', 'tl__price', money(t.price));
      td.appendChild(b);
      pr.appendChild(td);
    });
    tbody.appendChild(pr);

    /* buy */
    var br = document.createElement('tr');
    var bth = el('th', null, '');
    bth.scope = 'row';
    br.appendChild(bth);
    TOOLS.forEach(function (t) {
      var td = document.createElement('td');
      var b = el('button', 'tl__add', 'Add to cart');
      b.type = 'button';
      var added = false;
      b.addEventListener('click', function () {
        if (added) { window.location.href = window.IFZU('cart-revamp.html'); return; }
        addToCart(t);
        added = true;
        b.classList.add('is-added');
        b.textContent = 'Added! View cart ->';
      });
      td.appendChild(b);
      br.appendChild(td);
    });
    tbody.appendChild(br);

    tbl.appendChild(tbody);
    host.appendChild(tbl);
  }

  /* ----------------------------------------------------------------------
     THE USE-CASE PICKER
     ---------------------------------------------------------------------- */
  function picker() {
    var host = document.getElementById('toolPicks');
    var grid = document.getElementById('toolGrid');
    if (!host || !grid) return;

    var all = [{ id: 'all', label: 'All kits' }].concat(USES);

    /* A use with no kit in it would be a dead button, so it is not drawn. */
    all = all.filter(function (u) {
      return u.id === 'all' || TOOLS.some(function (t) { return t.uses.indexOf(u.id) > -1; });
    });

    all.forEach(function (u) {
      var b = el('button', 'tl__pick');
      b.type = 'button';
      b.setAttribute('aria-pressed', u.id === 'all' ? 'true' : 'false');
      b.appendChild(document.createTextNode(u.label));

      var n = u.id === 'all'
        ? TOOLS.length
        : TOOLS.filter(function (t) { return t.uses.indexOf(u.id) > -1; }).length;
      b.appendChild(el('b', null, String(n)));

      b.addEventListener('click', function () {
        host.querySelectorAll('.tl__pick').forEach(function (o) {
          o.setAttribute('aria-pressed', 'false');
        });
        b.setAttribute('aria-pressed', 'true');

        grid.querySelectorAll('.acc--tool').forEach(function (c) {
          var uses = (c.getAttribute('data-uses') || '').split(' ');
          c.hidden = !(u.id === 'all' || uses.indexOf(u.id) > -1);
        });
      });

      host.appendChild(b);
    });
  }

  function init() {
    var grid = document.getElementById('toolGrid');
    if (grid) TOOLS.forEach(function (t) { grid.appendChild(card(t)); });
    picker();
    table();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
